import { forwardRef } from "react";
import Webcam from "react-webcam";

export type WebcamFacingMode = "user" | "environment";

export const WEBCAM_SCREENSHOT_FORMAT = "image/jpeg";
export const WEBCAM_SCREENSHOT_QUALITY = 0.95;
export const WEBCAM_SCREENSHOT_WIDTH = 1536;
export const WEBCAM_SCREENSHOT_HEIGHT = 1152;

export function webcamVideoConstraints(facingMode: WebcamFacingMode): MediaTrackConstraints {
  return {
    width: { ideal: 1536, min: 1440 },
    height: { ideal: 1152, min: 1080 },
    aspectRatio: { ideal: 4 / 3 },
    facingMode
  };
}

export function stopWebcam(webcam: Webcam | null, streamRef: { current: MediaStream | null }) {
  const stopStream = (stream: MediaStream | null | undefined) => {
    if (!stream) return;
    stream.getTracks().forEach((track) => {
      track.stop();
    });
  };

  stopStream(streamRef.current);
  streamRef.current = null;

  stopStream(webcam?.stream ?? null);
  if (webcam?.stream) {
    webcam.stream = null;
  }

  if (webcam?.video?.srcObject) {
    const video = webcam.video;
    stopStream(video.srcObject as MediaStream);
    video.srcObject = null;
  }
}

export function snapshotWebcamDataUrl(webcam: Webcam | null, facingMode: WebcamFacingMode): string | null {
  const video = webcam?.video;
  if (!video) return null;

  const { videoWidth, videoHeight } = video;
  if (!videoWidth || !videoHeight) return null;

  const canvas = document.createElement("canvas");
  canvas.width = videoWidth;
  canvas.height = videoHeight;
  const context = canvas.getContext("2d");
  if (!context) return null;

  if (facingMode === "user") {
    context.translate(videoWidth, 0);
    context.scale(-1, 1);
  }
  context.drawImage(video, 0, 0, videoWidth, videoHeight);

  return canvas.toDataURL(WEBCAM_SCREENSHOT_FORMAT, WEBCAM_SCREENSHOT_QUALITY);
}

export async function jpegFileFromDataUrl(dataUrl: string, filename = "notes.jpg"): Promise<File> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: WEBCAM_SCREENSHOT_FORMAT });
}

type WebcamPreviewProps = {
  facingMode: WebcamFacingMode;
  className?: string;
  "data-testid"?: string;
  onUserMedia: (stream: MediaStream) => void;
  onUserMediaError: (error: string | DOMException) => void;
};

export const WebcamPreview = forwardRef<Webcam, WebcamPreviewProps>(function WebcamPreview(
  { facingMode, className, onUserMedia, onUserMediaError, "data-testid": testId },
  ref
) {
  return (
    <Webcam
      ref={ref}
      audio={false}
      screenshotFormat={WEBCAM_SCREENSHOT_FORMAT}
      screenshotQuality={WEBCAM_SCREENSHOT_QUALITY}
      videoConstraints={webcamVideoConstraints(facingMode)}
      onUserMedia={onUserMedia}
      onUserMediaError={onUserMediaError}
      forceScreenshotSourceSize={false}
      minScreenshotWidth={WEBCAM_SCREENSHOT_WIDTH}
      minScreenshotHeight={WEBCAM_SCREENSHOT_HEIGHT}
      mirrored={facingMode === "user"}
      className={className}
      data-testid={testId}
    />
  );
});
